/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'

interface UiAlertConfirmationModalProps {
    title: string
    message: string | React.ReactNode
    onConfirm: () => void | Promise<void>
    onCancel: () => void
    confirmLabel?: string
    cancelLabel?: string
    confirmStyle?: UiButtonStyle
    isSubmitting?: boolean
}

const UiAlertConfirmationModal: React.FC<UiAlertConfirmationModalProps> = ({
    title,
    message,
    onConfirm,
    onCancel,
    confirmLabel,
    cancelLabel,
    confirmStyle = UiButtonStyle.Primary,
    isSubmitting = false,
}) => {
    const { t } = useTranslation()
    const [isInternalSubmitting, setIsInternalSubmitting] = React.useState(false)

    const isLoading = isSubmitting || isInternalSubmitting

    const handleConfirm = () => {
        if (isLoading) {
            return
        }

        window.setTimeout(() => {
            const result = onConfirm()

            if (result && typeof (result as Promise<void>).then === 'function') {
                setIsInternalSubmitting(true)
                ;(result as Promise<void>).finally(() => {
                    setIsInternalSubmitting(false)
                })
            }
        }, 0)
    }

    return (
        <div className="UiAlertConfirmationModal p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-8">{message}</div>

            <div className="flex justify-end gap-3">
                <UiButton
                    style={UiButtonStyle.Secondary}
                    size={UiButtonSize.Medium}
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    {cancelLabel || t('No')}
                </UiButton>
                <UiButton
                    style={confirmStyle}
                    size={UiButtonSize.Medium}
                    onClick={handleConfirm}
                    disabled={isLoading}
                    loading={isLoading}
                >
                    {confirmLabel || t('Yes')}
                </UiButton>
            </div>
        </div>
    )
}

export default UiAlertConfirmationModal
